import {
    patchState,
    signalStore,
    withComputed,
    withHooks,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { CounterPartyProfileForm } from '../models/individual-business';
import { summary } from '../models/summary';
import { computed, inject } from '@angular/core';
import { AccountService } from '../services/account.service';
import { ProfilePaymentChannel } from '../models/counter-party-payment-channel';
import { Product } from '../models/product';
import { ComputeEvent, Events } from '../models/events';
import { ContractResponse } from '../models/contract-response';
import { rxResource } from '@angular/core/rxjs-interop';
import { Contracts } from '../models/contracts';
import { FormGroup } from '@angular/forms';

// Define our store state
export interface ContractCreateState {
    profileForm: CounterPartyProfileForm | null;
    profilePaymentChannel: ProfilePaymentChannel | null;
    terms: any;
    createdContractResponse: ContractResponse[] | null;
    computeContract: ComputeEvent[] | null;
    executeContract: Events[] | null;
    summary: summary | null;
    currentStep: number;
    isLoading: boolean;
    error: string | null;
    contractUUID: string | null;
    paymentChannelID: string | null;
    profileID: string | null;
    createContractDetails: {
        contractFieldConfig:
            | {
                  key: string;
                  label: string;
                  type: 'text' | 'date' | 'number';
                  hidden: boolean;
                  readOnly: boolean;
              }[]
            | null;

        contractFormData: FormGroup | null; // Optional field for additional contract data
    };
}

interface CreateContractStepsState {
    contractCreateState: ContractCreateState;
    loading: boolean;
    contractSearchQuery: { query: string };
    productSearchQuery: { query: string };
    product: Product | null;
}

const initialState: CreateContractStepsState = {
    contractCreateState: {
        profileForm: null,
        profilePaymentChannel: null,
        terms: null,
        createdContractResponse: null,
        computeContract: null,
        executeContract: null,
        summary: null,
        paymentChannelID: null,
        contractUUID: null,
        profileID: null,
        currentStep: 0,
        isLoading: false,
        error: null,
        createContractDetails: {
            contractFieldConfig: null, // Initialize as null
            contractFormData: null, // Initialize as null
        },
    },
    loading: false,
    product: null,

    contractSearchQuery: { query: 'All' },
    productSearchQuery: { query: 'All' },
};

export const AccountStore = signalStore(
    withState(initialState),
    withProps((store, _service = inject(AccountService)) => ({
        _service: inject(AccountService),
        _getContracts: rxResource<
            { status: number; message: string; data: Contracts[] },
            { query: string }
        >({
            request: () => store.contractSearchQuery(),
            loader: ({ request: searchQuery, abortSignal }) => {
                return _service.getAllContracts(searchQuery.query);
            },
        }),
        _getProducts: rxResource<
            { status: number; message: string; data: Product[] },
            { query: string }
        >({
            request: () => store.contractSearchQuery(),
            loader: ({ request: searchQuery, abortSignal }) => {
                return _service.getAllProducts(searchQuery.query);
            },
        }),
    })),
    withComputed((store) => ({
        computedContractResource: computed(() => store._getContracts),
        computedProductResource: computed(() => store._getProducts),
    })),

    withMethods((store) => ({
        startLoading(): void {
            patchState(store, { loading: true });
        },
        stopLoading(): void {
            patchState(store, () => ({ loading: false }));
        },

        resetStore() {
            patchState(store, {
                contractCreateState: {
                    profileForm: null,
                    profilePaymentChannel: null,
                    terms: null,
                    createdContractResponse: null,
                    computeContract: null,
                    executeContract: null,
                    summary: null,
                    paymentChannelID: null,
                    profileID: null,
                    currentStep: 0,
                    isLoading: false,
                    error: null,
                    contractUUID: null,
                    createContractDetails: {
                        contractFieldConfig: null,
                        contractFormData: null,
                    },
                },
            });
        },

        setCounterPartyProfile(
            profileID: string,
            formData?: CounterPartyProfileForm
        ): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    profileForm: formData!,
                    profileID,
                },
            });
        },

        setTerms(terms: any): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    terms,
                },
            });
        },

        refreshContracts(): void {
            store._getContracts.reload();
        },

        setProfilePaymentChannel(
            paymentChannel: ProfilePaymentChannel,
            paymentChannelID: string
        ): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    profilePaymentChannel: paymentChannel,
                    paymentChannelID,
                },
            });
        },

        setCreatedContract(data: ContractResponse[]): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    createdContractResponse: data,
                },
            });
        },

        setComputeContract(events: ComputeEvent[] | null): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    computeContract: events,
                },
            });
        },

        setCreateContractDetails(
            fieldConfig: any,
            formData: FormGroup | null
        ): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    createContractDetails: {
                        contractFieldConfig: fieldConfig,
                        contractFormData: formData,
                    },
                },
            });
        },

        setExecuteContract(events: Events[]): void {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    executeContract: events,
                },
            });
        },

        setProduct(product: Product): void {
            patchState(store, {
                product: { ...product },
            });
        },

        setContractUUID(contractUUID: string) {
            patchState(store, {
                contractCreateState: {
                    ...store.contractCreateState(),
                    contractUUID,
                },
            });
        },

        // Method to go to a specific step (if valid)
        goToStep(step: number) {
            if (step <= store.contractCreateState.currentStep()) {
                patchState(store, {
                    contractCreateState: {
                        ...store.contractCreateState(),
                        currentStep: step,
                        error: null,
                    },
                });
            }
        },
        // Method to save step 2 data
    }))
    // withHooks((store) => {
    //     return {
    //         onInit() {
    //             console.log('AccountStore initialized');
    //         },
    //     };
    // })
);
